/**
 * Pure clip editing operations. Every function returns a NEW clip object and never mutates its
 * inputs, so the undo/redo system can keep old snapshots safely.
 *
 * Invariants enforced here: frames are integers >= 0, unique per bone, sorted ascending;
 * rotations are normalized; `durationFrames` is never shorter than the last keyframe;
 * tracks hold rotation only.
 */

import { AppError } from './errors';
import { newId } from './ids';
import { isCanonicalBoneId } from './canonicalRig';
import { mirrorEasing } from './easing';
import { normalizeQuat } from './quat';
import {
  CANONICAL_BONE_IDS,
  DEFAULT_EASING,
  DEFAULT_FPS,
  RIG_VERSION,
  type BoneRotationTrack,
  type CanonicalAnimationClip,
  type CanonicalBoneId,
  type ClipEvent,
  type ClipEventData,
  type ClipboardPayload,
  type KeyframeSelection,
  type QuaternionTuple,
  type RotationKeyframe,
  type TweenPreset,
} from './types';

type TrackMap = Partial<Record<CanonicalBoneId, BoneRotationTrack>>;

function intFrame(frame: number): number {
  const rounded = Math.round(frame);
  return rounded < 0 ? 0 : rounded;
}

/** A finite own `hipsY`, or `undefined`. The channel is optional everywhere it appears. */
function readHipsY(k: { hipsY?: number }): number | undefined {
  return typeof k.hipsY === 'number' && Number.isFinite(k.hipsY) ? k.hipsY : undefined;
}

/**
 * Every op below copies keys through here, so `hipsY` rides its key through move / reverse /
 * scale / retime unchanged — a key is one unit of authored work, and losing half of it in a drag
 * is exactly the silent kind of loss this module exists to prevent.
 */
function copyKeyframe(k: RotationKeyframe): RotationKeyframe {
  const copy: RotationKeyframe = {
    frame: k.frame,
    rotation: [k.rotation[0], k.rotation[1], k.rotation[2], k.rotation[3]],
    easing: k.easing,
  };
  const hipsY = readHipsY(k);
  if (hipsY !== undefined) copy.hipsY = hipsY;
  return copy;
}

function sortKeys(keys: RotationKeyframe[]): RotationKeyframe[] {
  return keys.slice().sort((a, b) => a.frame - b.frame);
}

/** Selection refs bucketed per bone as integer frame sets (the shape every multi-key op wants). */
function selectedFramesByBone(selection: KeyframeSelection): Map<CanonicalBoneId, Set<number>> {
  const byBone = new Map<CanonicalBoneId, Set<number>>();
  for (const ref of selection) {
    let set = byBone.get(ref.bone);
    if (!set) {
      set = new Set<number>();
      byBone.set(ref.bone, set);
    }
    set.add(intFrame(ref.frame));
  }
  return byBone;
}

/** Highest keyframe frame across all tracks, or 0 when the clip has no keyframes. */
export function lastKeyframeFrame(clip: CanonicalAnimationClip): number {
  let last = 0;
  for (const bone of Object.keys(clip.tracks) as CanonicalBoneId[]) {
    const track = clip.tracks[bone];
    if (!track || track.keyframes.length === 0) continue;
    const frame = track.keyframes[track.keyframes.length - 1].frame;
    if (frame > last) last = frame;
  }
  return last;
}

function lastFrameOfTracks(tracks: TrackMap): number {
  let last = 0;
  for (const bone of Object.keys(tracks) as CanonicalBoneId[]) {
    const track = tracks[bone];
    if (!track || track.keyframes.length === 0) continue;
    const frame = track.keyframes[track.keyframes.length - 1].frame;
    if (frame > last) last = frame;
  }
  return last;
}

function dropEmptyTracks(tracks: TrackMap): TrackMap {
  const out: TrackMap = {};
  for (const bone of Object.keys(tracks) as CanonicalBoneId[]) {
    const track = tracks[bone];
    if (track && track.keyframes.length > 0) out[bone] = track;
  }
  return out;
}

function withTracks(
  clip: CanonicalAnimationClip,
  tracks: TrackMap,
  durationFrames?: number,
): CanonicalAnimationClip {
  const cleaned = dropEmptyTracks(tracks);
  const wanted = durationFrames ?? clip.durationFrames;
  const duration = Math.max(wanted, lastFrameOfTracks(cleaned));
  return {
    ...clip,
    tracks: cleaned,
    durationFrames: duration,
    updatedAt: Date.now(),
  };
}

export function createEmptyClip(
  name: string,
  fps: number = DEFAULT_FPS,
  durationFrames: number = fps,
): CanonicalAnimationClip {
  const now = Date.now();
  return {
    id: newId(),
    name,
    fps,
    durationFrames: Math.max(0, Math.round(durationFrames)),
    loop: true,
    tracks: {},
    createdAt: now,
    updatedAt: now,
  };
}

// ---------------------------------------------------------------------------
// Clip events
//
// Events are clip-scoped markers, not keyframes, and the difference drives every rule here:
//
//  - Two events MAY share a frame (two things can happen on one frame), so nothing is ever
//    dropped for colliding — unlike keyframes, where a collision means one rotation wins.
//  - They are identified by `(frame, name)`, so every editing op takes that pair as its ref and
//    exact duplicates are collapsed.
//  - They are NOT part of a keyframe selection, so the selection-scoped ops (`moveKeyframes`,
//    `reverseKeyframes`, `scaleKeyframes`, `pasteKeyframes`) deliberately leave them alone. Only
//    the clip-wide ops — `changeFps`, `setDuration`, `normalizeClip` — touch them.
// ---------------------------------------------------------------------------

/** The highest frame an event may legally occupy on this clip. */
export function lastLegalEventFrame(clip: Pick<CanonicalAnimationClip, 'durationFrames' | 'loop'>): number {
  const duration = Math.max(0, Math.round(clip.durationFrames));
  // Half-open on a looping clip: frame N is frame 0, so an event there would double-fire.
  return clip.loop ? Math.max(0, duration - 1) : duration;
}

function copyEvent(e: ClipEvent): ClipEvent {
  return e.data ? { frame: e.frame, name: e.name, data: { ...e.data } } : { frame: e.frame, name: e.name };
}

function sortEvents(events: ClipEvent[]): ClipEvent[] {
  return events
    .slice()
    .sort((a, b) => (a.frame !== b.frame ? a.frame - b.frame : a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
}

/**
 * Integer frames in range, trimmed non-empty names, exact `(frame, name)` duplicates collapsed
 * (last wins), sorted. Returns `undefined` when nothing survives, so the field disappears from
 * the clip rather than sitting there as an empty array that would serialize.
 */
export function normalizeEvents(
  events: readonly ClipEvent[] | undefined,
  clip: Pick<CanonicalAnimationClip, 'durationFrames' | 'loop'>,
): ClipEvent[] | undefined {
  if (!events || events.length === 0) return undefined;
  const limit = lastLegalEventFrame(clip);
  const bucket = new Map<string, ClipEvent>();

  for (const raw of events) {
    if (!raw || typeof raw.name !== 'string') continue;
    const name = raw.name.trim();
    if (name.length === 0) continue;
    const frame = Math.min(limit, intFrame(Number.isFinite(raw.frame) ? raw.frame : 0));
    bucket.set(`${frame}|${name}`, { frame, name, ...(raw.data ? { data: { ...raw.data } } : {}) });
  }

  const out = sortEvents([...bucket.values()]);
  return out.length > 0 ? out : undefined;
}

function withEvents(
  clip: CanonicalAnimationClip,
  events: readonly ClipEvent[] | undefined,
): CanonicalAnimationClip {
  const normalized = normalizeEvents(events, clip);
  const next: CanonicalAnimationClip = { ...clip, updatedAt: Date.now() };
  if (normalized) next.events = normalized;
  else delete next.events;
  return next;
}

/** Add an event. A `(frame, name)` pair that already exists is replaced, not duplicated. */
export function addClipEvent(
  clip: CanonicalAnimationClip,
  frame: number,
  name: string,
  data?: ClipEventData,
): CanonicalAnimationClip {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    throw new AppError('INVALID_EVENT_NAME', 'An animation event needs a name.');
  }
  return withEvents(clip, [
    ...(clip.events ?? []),
    { frame: intFrame(frame), name: trimmed, ...(data ? { data } : {}) },
  ]);
}

/** Remove the event at `(frame, name)`. A ref that matches nothing returns the clip unchanged. */
export function removeClipEvent(
  clip: CanonicalAnimationClip,
  frame: number,
  name: string,
): CanonicalAnimationClip {
  const events = clip.events ?? [];
  const target = intFrame(frame);
  const kept = events.filter((e) => !(e.frame === target && e.name === name));
  return kept.length === events.length ? clip : withEvents(clip, kept);
}

/**
 * Move and/or rename the event at `(frame, name)`, keeping its payload. Returns the clip
 * unchanged when the ref matches nothing — the editor re-resolves its selection after every
 * commit, and a stale ref must not throw at the user.
 */
export function updateClipEvent(
  clip: CanonicalAnimationClip,
  ref: { frame: number; name: string },
  next: { frame?: number; name?: string; data?: ClipEventData | undefined },
): CanonicalAnimationClip {
  const events = clip.events ?? [];
  const from = intFrame(ref.frame);
  const index = events.findIndex((e) => e.frame === from && e.name === ref.name);
  if (index < 0) return clip;

  const current = events[index];
  const name = (next.name ?? current.name).trim();
  if (name.length === 0) {
    throw new AppError('INVALID_EVENT_NAME', 'An animation event needs a name.');
  }
  const data = 'data' in next ? next.data : current.data;

  const replaced = events.slice();
  replaced[index] = {
    frame: intFrame(next.frame ?? current.frame),
    name,
    ...(data && Object.keys(data).length > 0 ? { data: { ...data } } : {}),
  };
  return withEvents(clip, replaced);
}

/** Every event at exactly `frame`, in name order. */
export function eventsAtFrame(clip: CanonicalAnimationClip, frame: number): ClipEvent[] {
  const target = intFrame(frame);
  return (clip.events ?? []).filter((e) => e.frame === target).map(copyEvent);
}

/** Highest event frame, or -1 when the clip has none. */
export function lastEventFrame(clip: CanonicalAnimationClip): number {
  let last = -1;
  for (const e of clip.events ?? []) if (e.frame > last) last = e.frame;
  return last;
}

/** Options form of `setKeyframe`'s trailing argument (the bare `TweenPreset` still works). */
export interface SetKeyframeOptions {
  easing?: TweenPreset;
  /**
   * Hips vertical offset for this key. `number` sets it (a non-finite number is treated as if it
   * were absent), `null` removes it, and **absent keeps whatever the key being replaced carried** —
   * so re-keying a rotation at a frame the user already crouched at does not quietly stand the
   * character back up. Ignored unless `bone === 'Hips'`.
   */
  hipsY?: number | null;
}

/**
 * Insert or replace a keyframe. The frame is rounded to an integer, the rotation normalized,
 * and `durationFrames` extended when the key lands past the current end.
 */
export function setKeyframe(
  clip: CanonicalAnimationClip,
  bone: CanonicalBoneId,
  frame: number,
  rotation: QuaternionTuple,
  easingOrOptions?: TweenPreset | SetKeyframeOptions,
): CanonicalAnimationClip {
  // A bare preset is the original signature and keeps its original behaviour exactly, including
  // the "keep the replaced key's hipsY" default.
  const options: SetKeyframeOptions =
    typeof easingOrOptions === 'string' ? { easing: easingOrOptions } : easingOrOptions ?? {};

  const f = intFrame(frame);
  const key: RotationKeyframe = {
    frame: f,
    rotation: normalizeQuat(rotation),
    easing: options.easing ?? DEFAULT_EASING,
  };

  const existing = clip.tracks[bone];

  if (bone === 'Hips') {
    const replaced = existing?.keyframes.find((k) => k.frame === f);
    const requested = options.hipsY;
    if (requested === null) {
      // Explicit removal: this key no longer says anything about hips height.
    } else if (typeof requested === 'number' && Number.isFinite(requested)) {
      key.hipsY = requested;
    } else if (replaced) {
      const kept = readHipsY(replaced);
      if (kept !== undefined) key.hipsY = kept;
    }
  }

  const keys = existing ? existing.keyframes.filter((k) => k.frame !== f).map(copyKeyframe) : [];
  keys.push(key);

  const tracks: TrackMap = { ...clip.tracks, [bone]: { keyframes: sortKeys(keys) } };
  return withTracks(clip, tracks);
}

export function deleteKeyframes(
  clip: CanonicalAnimationClip,
  selection: KeyframeSelection,
): CanonicalAnimationClip {
  if (selection.length === 0) return clip;

  const byBone = new Map<CanonicalBoneId, Set<number>>();
  for (const ref of selection) {
    let set = byBone.get(ref.bone);
    if (!set) {
      set = new Set<number>();
      byBone.set(ref.bone, set);
    }
    set.add(intFrame(ref.frame));
  }

  const tracks: TrackMap = { ...clip.tracks };
  for (const [bone, frames] of byBone) {
    const track = tracks[bone];
    if (!track) continue;
    tracks[bone] = { keyframes: track.keyframes.filter((k) => !frames.has(k.frame)) };
  }
  return withTracks(clip, tracks);
}

/**
 * Shift the selected keyframes by `deltaFrames` (rounded to an integer, result clamped to >= 0).
 * When a moved key lands on an existing key, the moved key wins.
 *
 * `droppedKeys` counts every key that loss cost: a moved key landing on an unselected key AND
 * moved keys collapsing onto each other (clamping at 0 can pile a whole selection onto frame 0).
 *
 * The counter is the point of this signature. `scaleKeyframes` and `changeFps` have returned one
 * from the start "so the UI can warn before committing"; `moveKeyframes` returned a bare clip,
 * and it was the ONE place in this module that destroyed keys without saying so. The audit drove
 * it on the real dope sheet (§2.2 #3): keys at 0/5/10 dragged back six frames became two keys,
 * the frame-0 rotation was gone, and there was no toast, no error and no console line. Undo did
 * bring all three back — the loss is recoverable, but only by a user who knows it happened. The
 * op itself is unchanged; only the report is new.
 */
export function moveKeyframes(
  clip: CanonicalAnimationClip,
  selection: KeyframeSelection,
  deltaFrames: number,
): { clip: CanonicalAnimationClip; droppedKeys: number } {
  const delta = Math.round(deltaFrames);
  if (selection.length === 0 || delta === 0) return { clip, droppedKeys: 0 };

  const byBone = new Map<CanonicalBoneId, Set<number>>();
  for (const ref of selection) {
    let set = byBone.get(ref.bone);
    if (!set) {
      set = new Set<number>();
      byBone.set(ref.bone, set);
    }
    set.add(intFrame(ref.frame));
  }

  const tracks: TrackMap = { ...clip.tracks };
  let droppedKeys = 0;

  for (const [bone, frames] of byBone) {
    const track = tracks[bone];
    if (!track) continue;

    const moved: RotationKeyframe[] = [];
    const stayed: RotationKeyframe[] = [];
    for (const k of track.keyframes) {
      if (frames.has(k.frame)) {
        const copy = copyKeyframe(k);
        copy.frame = Math.max(0, k.frame + delta);
        moved.push(copy);
      } else {
        stayed.push(copyKeyframe(k));
      }
    }
    if (moved.length === 0) continue;

    // Moved keys win collisions — both against static keys and against each other
    // (clamping at 0 can collapse several moved keys onto frame 0; the later one wins).
    const merged = new Map<number, RotationKeyframe>();
    for (const k of stayed) merged.set(k.frame, k);
    const takenByMoved = new Set<number>();
    // Ascending source order: the later key wins, and every collapse is counted once — the same
    // shape as `scaleKeyframes`, so the two counters mean exactly the same thing.
    for (const k of moved) {
      if (takenByMoved.has(k.frame) || merged.has(k.frame)) droppedKeys++;
      takenByMoved.add(k.frame);
      merged.set(k.frame, k);
    }

    tracks[bone] = { keyframes: sortKeys([...merged.values()]) };
  }

  return { clip: withTracks(clip, tracks), droppedKeys };
}

/**
 * Reverse the selected keyframes in time.
 *
 * The span is GLOBAL — `min..max` over every selected key in the clip, not per bone — so a
 * multi-bone selection stays in sync instead of each track folding around its own centre.
 * Each selected key moves to `min + max - frame`; unselected keys (including ones inside the
 * span) stay where they are and LOSE a collision with a reversed key, exactly like
 * `moveKeyframes`. `overwrittenKeys` counts those losses.
 *
 * Easings SHIFT by one key rather than mirroring in place. The evaluator takes a segment's
 * easing from its LEFT key (`evaluator.ts`, `interpolate` uses `a.easing`), so with selected
 * keys `k_0..k_{n-1}` ascending and `e_j` governing `[k_j, k_{j+1}]`, the reversed key at
 * ascending position `i` governs the reverse of `[k_{n-2-i}, k_{n-1-i}]` and therefore carries
 * `mirrorEasing(e_{n-2-i})`. The new last key governs nothing; it gets `mirrorEasing(e_0)`.
 *
 * Caveat: `step` has no mirror in `TWEEN_PRESETS` (its reverse is hold-right), so a reversed
 * `step` segment holds the other end. See `mirrorEasing` in `easing.ts` — every other preset
 * round-trips exactly.
 */
export function reverseKeyframes(
  clip: CanonicalAnimationClip,
  selection: KeyframeSelection,
): { clip: CanonicalAnimationClip; overwrittenKeys: number } {
  const byBone = selectedFramesByBone(selection);

  // Only keys that actually exist define the span — a stale ref must not stretch the reversal.
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let selectedCount = 0;
  for (const [bone, frames] of byBone) {
    const track = clip.tracks[bone];
    if (!track) continue;
    for (const k of track.keyframes) {
      if (!frames.has(k.frame)) continue;
      selectedCount++;
      if (k.frame < min) min = k.frame;
      if (k.frame > max) max = k.frame;
    }
  }
  if (selectedCount < 2 || min === max) return { clip, overwrittenKeys: 0 };

  const tracks: TrackMap = { ...clip.tracks };
  let overwrittenKeys = 0;

  for (const [bone, frames] of byBone) {
    const track = tracks[bone];
    if (!track) continue;

    const selected: RotationKeyframe[] = [];
    const stayed: RotationKeyframe[] = [];
    for (const k of track.keyframes) {
      if (frames.has(k.frame)) selected.push(k);
      else stayed.push(copyKeyframe(k));
    }
    if (selected.length === 0) continue;

    // `selected` is ascending (tracks are sorted); the reversed key at position i takes the
    // rotation of the old key n-1-i and the mirrored easing of the old key n-2-i.
    const n = selected.length;
    const reversed: RotationKeyframe[] = [];
    for (let i = 0; i < n; i++) {
      const source = selected[n - 1 - i];
      const easingSource = i <= n - 2 ? selected[n - 2 - i] : selected[0];
      const copy = copyKeyframe(source);
      copy.frame = min + max - source.frame;
      copy.easing = mirrorEasing(easingSource.easing);
      reversed.push(copy);
    }

    const merged = new Map<number, RotationKeyframe>();
    for (const k of stayed) merged.set(k.frame, k);
    for (const k of reversed) {
      if (merged.has(k.frame)) overwrittenKeys++;
      merged.set(k.frame, k);
    }

    tracks[bone] = { keyframes: sortKeys([...merged.values()]) };
  }

  return { clip: withTracks(clip, tracks), overwrittenKeys };
}

export const SCALE_FACTOR_MIN = 0.05;
export const SCALE_FACTOR_MAX = 20;

/**
 * Validate a time-scale factor and clamp it into `[SCALE_FACTOR_MIN, SCALE_FACTOR_MAX]`.
 * Throws `AppError('INVALID_SCALE_FACTOR')` for a non-finite or non-positive factor — a
 * negative factor would mean "reverse", which is `reverseKeyframes`, not this op.
 */
export function clampScaleFactor(factor: number): number {
  if (!Number.isFinite(factor) || factor <= 0) {
    throw new AppError(
      'INVALID_SCALE_FACTOR',
      `The time scale must be a positive number (got ${factor}).`,
    );
  }
  return Math.min(SCALE_FACTOR_MAX, Math.max(SCALE_FACTOR_MIN, factor));
}

/**
 * Time-scale the selected keyframes around the EARLIEST selected frame (global across bones):
 * `frame' = pivot + round((frame - pivot) * factor)`.
 *
 * Rounding to whole frames means a factor below 1 can collapse neighbours; the later (higher
 * original frame) key wins, as everywhere else. `droppedKeys` counts both kinds of loss —
 * scaled keys colliding with each other AND scaled keys landing on unselected keys — so the UI
 * can warn. The duration grows when keys pass the end (`withTracks`).
 */
export function scaleKeyframes(
  clip: CanonicalAnimationClip,
  selection: KeyframeSelection,
  factor: number,
): { clip: CanonicalAnimationClip; droppedKeys: number } {
  const scale = clampScaleFactor(factor);
  const byBone = selectedFramesByBone(selection);

  let pivot = Number.POSITIVE_INFINITY;
  let selectedCount = 0;
  for (const [bone, frames] of byBone) {
    const track = clip.tracks[bone];
    if (!track) continue;
    for (const k of track.keyframes) {
      if (!frames.has(k.frame)) continue;
      selectedCount++;
      if (k.frame < pivot) pivot = k.frame;
    }
  }
  if (selectedCount < 2 || scale === 1) return { clip, droppedKeys: 0 };

  const tracks: TrackMap = { ...clip.tracks };
  let droppedKeys = 0;

  for (const [bone, frames] of byBone) {
    const track = tracks[bone];
    if (!track) continue;

    const scaled: RotationKeyframe[] = [];
    const stayed: RotationKeyframe[] = [];
    for (const k of track.keyframes) {
      if (frames.has(k.frame)) {
        const copy = copyKeyframe(k);
        copy.frame = Math.max(0, pivot + Math.round((k.frame - pivot) * scale));
        scaled.push(copy);
      } else {
        stayed.push(copyKeyframe(k));
      }
    }
    if (scaled.length === 0) continue;

    const merged = new Map<number, RotationKeyframe>();
    for (const k of stayed) merged.set(k.frame, k);
    const takenByScaled = new Set<number>();
    // Ascending source order: the later key wins, and every collapse is counted once.
    for (const k of scaled) {
      if (takenByScaled.has(k.frame) || merged.has(k.frame)) droppedKeys++;
      takenByScaled.add(k.frame);
      merged.set(k.frame, k);
    }

    tracks[bone] = { keyframes: sortKeys([...merged.values()]) };
  }

  return { clip: withTracks(clip, tracks), droppedKeys };
}

export function setEasing(
  clip: CanonicalAnimationClip,
  selection: KeyframeSelection,
  preset: TweenPreset,
): CanonicalAnimationClip {
  if (selection.length === 0) return clip;

  const byBone = new Map<CanonicalBoneId, Set<number>>();
  for (const ref of selection) {
    let set = byBone.get(ref.bone);
    if (!set) {
      set = new Set<number>();
      byBone.set(ref.bone, set);
    }
    set.add(intFrame(ref.frame));
  }

  const tracks: TrackMap = { ...clip.tracks };
  for (const [bone, frames] of byBone) {
    const track = tracks[bone];
    if (!track) continue;
    tracks[bone] = {
      keyframes: track.keyframes.map((k) => {
        if (!frames.has(k.frame)) return k;
        const copy = copyKeyframe(k);
        copy.easing = preset;
        return copy;
      }),
    };
  }
  return withTracks(clip, tracks);
}

/** Copy the selected keyframes; frames become offsets relative to the earliest selected frame. */
export function copyKeyframes(
  clip: CanonicalAnimationClip,
  selection: KeyframeSelection,
): ClipboardPayload {
  const picked: { bone: CanonicalBoneId; key: RotationKeyframe }[] = [];
  for (const ref of selection) {
    const track = clip.tracks[ref.bone];
    if (!track) continue;
    const frame = intFrame(ref.frame);
    const key = track.keyframes.find((k) => k.frame === frame);
    if (key) picked.push({ bone: ref.bone, key });
  }

  let min = 0;
  if (picked.length > 0) {
    min = picked[0].key.frame;
    for (const p of picked) if (p.key.frame < min) min = p.key.frame;
  }

  return {
    kind: 'animstatex/keyframes',
    rigVersion: RIG_VERSION,
    fps: clip.fps,
    keyframes: picked
      .map((p) => {
        const hipsY = readHipsY(p.key);
        return {
          bone: p.bone,
          frameOffset: p.key.frame - min,
          rotation: [
            p.key.rotation[0],
            p.key.rotation[1],
            p.key.rotation[2],
            p.key.rotation[3],
          ] as QuaternionTuple,
          easing: p.key.easing,
          // Carried so a copied crouch keeps its depth; `pasteKeyframes` decides whether the
          // destination bone may receive it.
          ...(hipsY !== undefined ? { hipsY } : {}),
        };
      })
      .sort((a, b) => a.frameOffset - b.frameOffset || a.bone.localeCompare(b.bone)),
  };
}

/** Paste a clipboard payload so its earliest keyframe lands on `atFrame`. Pasted keys win. */
export function pasteKeyframes(
  clip: CanonicalAnimationClip,
  payload: ClipboardPayload,
  atFrame: number,
  boneRemap?: Partial<Record<CanonicalBoneId, CanonicalBoneId>>,
): CanonicalAnimationClip {
  if (payload.keyframes.length === 0) return clip;
  const base = intFrame(atFrame);

  const tracks: TrackMap = { ...clip.tracks };
  const merged = new Map<CanonicalBoneId, Map<number, RotationKeyframe>>();

  const bucketFor = (bone: CanonicalBoneId): Map<number, RotationKeyframe> => {
    let bucket = merged.get(bone);
    if (!bucket) {
      bucket = new Map<number, RotationKeyframe>();
      const existing = tracks[bone];
      if (existing) for (const k of existing.keyframes) bucket.set(k.frame, copyKeyframe(k));
      merged.set(bone, bucket);
    }
    return bucket;
  };

  for (const entry of payload.keyframes) {
    const target = boneRemap?.[entry.bone] ?? entry.bone;
    if (!isCanonicalBoneId(target)) continue;
    const frame = Math.max(0, base + Math.round(entry.frameOffset));
    const pasted: RotationKeyframe = {
      frame,
      rotation: normalizeQuat(entry.rotation),
      easing: entry.easing,
    };
    // Only the Hips track may carry the offset: pasting a Hips key onto an arm (or through a
    // mirror remap) must not smuggle a hips height onto a bone that has no such channel.
    if (target === 'Hips') {
      const hipsY = readHipsY(entry);
      if (hipsY !== undefined) pasted.hipsY = hipsY;
    }
    bucketFor(target).set(frame, pasted);
  }

  for (const [bone, bucket] of merged) {
    tracks[bone] = { keyframes: sortKeys([...bucket.values()]) };
  }

  return withTracks(clip, tracks);
}

/**
 * Retime a clip to a new fps (plan D10): `frame -> round(frame * newFps / oldFps)`.
 * Collisions keep the later (higher original frame) key; the duration rescales the same way.
 * `droppedKeys` counts the keys lost to collisions, so the UI can warn before committing.
 */
export function changeFps(
  clip: CanonicalAnimationClip,
  newFps: number,
): { clip: CanonicalAnimationClip; droppedKeys: number } {
  const fps = Math.round(newFps);
  if (!Number.isFinite(fps) || fps < 1 || fps > 240) {
    throw new AppError('INVALID_FPS', `FPS must be an integer between 1 and 240 (got ${newFps}).`);
  }
  if (fps === clip.fps) return { clip, droppedKeys: 0 };

  const ratio = fps / clip.fps;
  const tracks: TrackMap = {};
  let droppedKeys = 0;

  for (const bone of Object.keys(clip.tracks) as CanonicalBoneId[]) {
    const track = clip.tracks[bone];
    if (!track) continue;
    const bucket = new Map<number, RotationKeyframe>();
    // Ascending source order means a later source key overwrites an earlier collision.
    for (const k of track.keyframes) {
      const frame = Math.max(0, Math.round(k.frame * ratio));
      if (bucket.has(frame)) droppedKeys++;
      const copy = copyKeyframe(k);
      copy.frame = frame;
      bucket.set(frame, copy);
    }
    tracks[bone] = { keyframes: sortKeys([...bucket.values()]) };
  }

  const duration = Math.max(0, Math.round(clip.durationFrames * ratio));
  const cleaned = dropEmptyTracks(tracks);
  const next: CanonicalAnimationClip = {
    ...clip,
    fps,
    tracks: cleaned,
    durationFrames: Math.max(duration, lastFrameOfTracks(cleaned)),
    updatedAt: Date.now(),
  };

  // Events are rescaled by the same ratio and then CLAMPED, because rounding can manufacture the
  // one position that is illegal: 30→15 fps on a looping 10-frame clip sends the duration to 5 and
  // an event at frame 9 to Math.round(4.5) = 5 — exactly `durationFrames`, where `wrapFrame` makes
  // it frame 0 and it fires twice every loop. `normalizeEvents` applies the clamp.
  // Colliding events are kept: unlike keyframes, two events may share a frame.
  const scaled = normalizeEvents(
    (clip.events ?? []).map((e) => ({ ...e, frame: Math.max(0, Math.round(e.frame * ratio)) })),
    next,
  );
  if (scaled) next.events = scaled;
  else delete next.events;

  return { clip: next, droppedKeys };
}

/** Set the clip length. Throws `AppError('DURATION_TOO_SHORT')` when it would cut keyframes off. */
export function setDuration(
  clip: CanonicalAnimationClip,
  durationFrames: number,
): CanonicalAnimationClip {
  const n = Math.round(durationFrames);
  if (!Number.isFinite(n) || n < 0) {
    throw new AppError('INVALID_DURATION', `Duration must be a frame count >= 0 (got ${durationFrames}).`);
  }
  const last = lastKeyframeFrame(clip);
  if (n < last) {
    throw new AppError(
      'DURATION_TOO_SHORT',
      `Duration ${n} is shorter than the last keyframe at frame ${last}.`,
      { requested: n, lastKeyframeFrame: last },
    );
  }

  // Events refuse the cut for the same reason keyframes do — silently losing a footstep marker is
  // worse than making the user move it. `lastLegalEventFrame` is one lower on a looping clip.
  const lastEvent = lastEventFrame(clip);
  const eventLimit = lastLegalEventFrame({ durationFrames: n, loop: clip.loop });
  if (lastEvent > eventLimit) {
    throw new AppError(
      'DURATION_TOO_SHORT',
      `Duration ${n} is shorter than the last animation event at frame ${lastEvent}.`,
      { requested: n, lastEventFrame: lastEvent },
    );
  }

  return { ...clip, durationFrames: n, updatedAt: Date.now() };
}

/**
 * Bring a clip back to its invariants: integer frames >= 0, sorted, unique (later wins),
 * normalized quaternions, no empty tracks, duration >= last keyframe, unknown bones dropped.
 */
export function normalizeClip(clip: CanonicalAnimationClip): CanonicalAnimationClip {
  const tracks: TrackMap = {};

  for (const bone of CANONICAL_BONE_IDS) {
    const track = clip.tracks[bone];
    if (!track || track.keyframes.length === 0) continue;

    const bucket = new Map<number, RotationKeyframe>();
    for (const k of track.keyframes) {
      const frame = intFrame(k.frame);
      const key: RotationKeyframe = {
        frame,
        rotation: normalizeQuat(k.rotation),
        easing: k.easing,
      };
      // This loop re-projects every key field by field, so `hipsY` has to be named here or it is
      // gone the moment a clip is normalized — which happens on every save. It survives ONLY on
      // the Hips track and only when finite; anywhere else the channel has no meaning.
      if (bone === 'Hips') {
        const hipsY = readHipsY(k);
        if (hipsY !== undefined) key.hipsY = hipsY;
      }
      bucket.set(frame, key);
    }
    const keys = sortKeys([...bucket.values()]);
    if (keys.length > 0) tracks[bone] = { keyframes: keys };
  }

  const duration = Math.max(
    0,
    Math.round(Number.isFinite(clip.durationFrames) ? clip.durationFrames : 0),
    lastFrameOfTracks(tracks),
  );

  const next: CanonicalAnimationClip = { ...clip, tracks, durationFrames: duration };
  const events = normalizeEvents(clip.events, next);
  if (events) next.events = events;
  else delete next.events;

  return next;
}

export function getKeyframeAt(
  clip: CanonicalAnimationClip,
  bone: CanonicalBoneId,
  frame: number,
): RotationKeyframe | undefined {
  const track = clip.tracks[bone];
  if (!track) return undefined;
  const f = intFrame(frame);
  return track.keyframes.find((k) => k.frame === f);
}

/**
 * The keyframes bracketing `frame` on a bone track. `prev` is the last key at or before the
 * frame, `next` the first key strictly after it.
 */
export function nearestKeyframes(
  clip: CanonicalAnimationClip,
  bone: CanonicalBoneId,
  frame: number,
): { prev?: RotationKeyframe; next?: RotationKeyframe } {
  const track = clip.tracks[bone];
  if (!track || track.keyframes.length === 0) return {};
  let prev: RotationKeyframe | undefined;
  let next: RotationKeyframe | undefined;
  for (const k of track.keyframes) {
    if (k.frame <= frame) prev = k;
    else {
      next = k;
      break;
    }
  }
  return { prev, next };
}
